export const biographyData = {
  summary: {
    personal: {
      name: "Omar Saad",
      location: "Oslo, Norway",
      languages: ["Arabic", "English", "Norwegian"],
      birth_date: "1998-05-07",
      nationality: "Norwegian"
    },
    professional: {
      current_role: "Senior Software Engineer",
      experience_years: 5,
      specializations: [
        "Software Engineering",
        "Infrastructure Development",
        "Machine Learning",
        "FinTech Solutions",
        "System Architecture"
      ],
      focus_areas: [
        "Scalable Backend Systems",
        "API Design and Development",
        "Database Architecture",
        "Cloud Infrastructure",
        "Performance Optimization"
      ]
    },
    education: {
      degree: "Master's in Software Engineering and Machine Learning",
      specialization: "Software Engineering and Machine Learning",
      university: "University of Bergen",
      graduation_year: 2023,
      location: "Bergen, Norway",
      additional_education: [
        "Advanced Machine Learning Algorithms",
        "Distributed Systems Design",
        "Software Architecture Patterns"
      ]
    },
    key_achievements: [
      "Led development of high-performance trading infrastructure",
      "Implemented ML-driven risk management systems",
      "Designed scalable microservices architecture",
      "Optimized system performance by 40%"
    ]
  },

  skills: {
    programming_languages: [
      {
        name: "Python",
        proficiency: 90,
        years_experience: 5,
        description: "Primary language for backend development, data processing, and ML"
      },
      {
        name: "JavaScript",
        proficiency: 85,
        years_experience: 4,
        description: "Full-stack web development, Node.js, and frontend applications"
      },
      {
        name: "TypeScript",
        proficiency: 80,
        years_experience: 3,
        description: "Type-safe JavaScript for large applications"
      },
      {
        name: "Java",
        proficiency: 75,
        years_experience: 3,
        description: "Enterprise applications and system development"
      },
      {
        name: "C++",
        proficiency: 70,
        years_experience: 2,
        description: "Performance-critical applications and algorithms"
      },
      {
        name: "Go",
        proficiency: 65,
        years_experience: 1,
        description: "Microservices and concurrent systems"
      }
    ],
    frameworks_libraries: [
      {
        name: "Flask",
        proficiency: 90,
        category: "Backend",
        description: "Lightweight web framework for Python APIs"
      },
      {
        name: "FastAPI",
        proficiency: 85,
        category: "Backend",
        description: "Modern, fast web framework for building APIs"
      },
      {
        name: "React",
        proficiency: 80,
        category: "Frontend",
        description: "Component-based UI library"
      },
      {
        name: "Node.js",
        proficiency: 80,
        category: "Backend",
        description: "JavaScript runtime for server-side development"
      },
      {
        name: "TensorFlow",
        proficiency: 75,
        category: "Machine Learning",
        description: "Deep learning and machine learning framework"
      },
      {
        name: "PyTorch",
        proficiency: 75,
        category: "Machine Learning",
        description: "Research-focused deep learning framework"
      }
    ],
    tools_technologies: [
      {
        name: "Docker",
        proficiency: 85,
        category: "DevOps",
        description: "Containerization and deployment"
      },
      {
        name: "AWS",
        proficiency: 80,
        category: "Cloud",
        description: "Cloud infrastructure and services"
      },
      {
        name: "PostgreSQL",
        proficiency: 85,
        category: "Database",
        description: "Advanced relational database management"
      },
      {
        name: "Redis",
        proficiency: 80,
        category: "Database",
        description: "In-memory data structure store"
      },
      {
        name: "Git",
        proficiency: 90,
        category: "Version Control",
        description: "Distributed version control system"
      }
    ]
  },

  experience: [
    {
      title: "Senior Software Engineer",
      company: "Luca Labs",
      period: "2023 – 2025",
      location: "Oslo, Norway",
      type: "Full-time",
      description: "Led software development in the FinTech sector, focusing on infrastructure development and machine learning applications for financial trading systems.",
      responsibilities: [
        "Design and implement scalable microservices architecture",
        "Lead development team of 4 engineers on critical trading platform features",
        "Optimize system performance and reduce latency in high-frequency trading systems",
        "Implement machine learning models for risk assessment and trading analysis",
        "Mentor junior developers and establish best practices"
      ],
      achievements: [
        "Improved system performance by 40% through optimization initiatives",
        "Reduced API response times from 200ms to 50ms average",
        "Successfully migrated legacy systems to cloud-native architecture",
        "Implemented automated testing pipeline reducing deployment time by 60%"
      ],
      technologies: ["Python", "FastAPI", "PostgreSQL", "Docker", "AWS", "Kubernetes", "Redis"]
    },
    {
      title: "Software Developer",
      company: "Tech Solutions AS",
      period: "2021 - 2023",
      location: "Bergen, Norway",
      type: "Full-time",
      description: "Full-stack development and machine learning implementation for various client projects and internal tools.",
      responsibilities: [
        "Develop and maintain web applications using modern frameworks",
        "Implement machine learning models for data analysis and prediction",
        "Collaborate with cross-functional teams on product development",
        "Participate in code reviews and maintain high code quality standards"
      ],
      achievements: [
        "Built ML-powered analytics platform serving 10,000+ daily users",
        "Contributed to 3 open-source projects with 500+ GitHub stars combined",
        "Reduced client onboarding time by 50% through automation"
      ],
      technologies: ["Python", "JavaScript", "React", "TensorFlow", "PostgreSQL", "Docker"]
    },
    {
      title: "Junior Developer",
      company: "StartupCorp",
      period: "2020 - 2021",
      location: "Bergen, Norway",
      type: "Part-time",
      description: "Entry-level position focusing on web development and learning modern software development practices.",
      responsibilities: [
        "Assist in developing web applications and APIs",
        "Write unit tests and maintain code documentation",
        "Support senior developers in various projects"
      ],
      achievements: [
        "Successfully completed 15+ feature implementations",
        "Maintained 95%+ code coverage in assigned modules"
      ],
      technologies: ["Python", "Flask", "JavaScript", "HTML/CSS", "Git"]
    }
  ],

  projects: [
    {
      title: "AI-Powered Portfolio Site",
      description: "This site — a personal portfolio with an AI chatbot backed by the OpenAI Agents SDK and a vector store for context retrieval. Vanilla JS/Vite frontend, Python/FastAPI backend, deployed on Kubernetes.",
      tech: ["Python", "FastAPI", "JavaScript", "Vite", "OpenAI", "Kubernetes"],
      github: "https://github.com/omarsaad98/omar-landingpage",
      live: "https://omarsaad.no"
    },
    {
      title: "Luca AI Agent System",
      description: "Production multi-agent AI platform for FinTech business intelligence at Luca Labs. Text-to-SQL pipeline enabling natural language queries over live financial data for business analysts.",
      tech: ["Python", "OpenAI Agents SDK", "FastAPI", "PostgreSQL", "Docker"],
      github: null,
      live: null
    },
    {
      title: "Smart Home Automation Platform",
      description: "Comprehensive Home Assistant deployment integrating IoT devices, energy monitoring, media server orchestration, and custom financial dashboards — all self-hosted with full Docker and NGINX stack.",
      tech: ["Home Assistant", "Python", "Docker", "NGINX", "IoT"],
      github: null,
      live: null
    }
  ]
};
